import React, { useEffect, useRef, MutableRefObject, useContext } from 'react'
import styled from 'styled-components'
import { giphyDarkestGrey } from '@giphy/js-brand'
import { IAnimationElement } from '../types'
import { ANIMATION_DRIFT_SPEED, ANIMATION_MIN_SCALE, ANIMATION_SCALE_DIFF, ANIMATION_SPEED } from '../constants'
import { getAnimationShape } from './animation-shapes'
import { AboutPageContext } from '../context'

const Canvas = styled.canvas`
    background: #000;
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
`

type Props = {
    onHeightUpdate: (height: number) => void
}

const AboutPageHeaderAnimation = ({ onHeightUpdate }: Props) => {
    const { windowWidth } = useContext(AboutPageContext)
    const canvasRef: MutableRefObject<HTMLCanvasElement | null> = useRef(null)

    // draw
    useEffect(() => {
        if (!canvasRef.current) return

        const canvas: HTMLCanvasElement = canvasRef.current
        const context: CanvasRenderingContext2D | null = canvas.getContext('2d')
        const height: number = canvas.offsetHeight
        const width: number = canvas.offsetWidth
        const elementCount: number = Math.ceil(width / 100)
        const elements: IAnimationElement[] = []
        const gradient: CanvasGradient | null = context ? context.createLinearGradient(0, 0, width, 0) : null
        let animationId: number | null = null

        if (gradient) {
            gradient.addColorStop(0, '#14132D')
            gradient.addColorStop(1, '#09202A')
        }

        const draw = () => {
            canvas.height = height
            canvas.width = width
            elements.forEach(element => {
                const { shape, x, y, scale, rotation, drift } = element
                if (!shape) return
                const shiftX: number = shape.width / 2
                const shiftY: number = shape.height / 2
                if (context) {
                    context.save()
                    context.fillStyle = '#fff'
                    context.translate(x, y)
                    context.scale(scale, scale)
                    context.rotate(rotation * (Math.PI / 180))
                    context.beginPath()
                    context.moveTo(shape.path[0][0] - shiftX, shape.path[0][1] - shiftY)
                    for (let i = 1; i < shape.path.length; i++) {
                        context.lineTo(shape.path[i][0] - shiftX, shape.path[i][1] - shiftY)
                    }
                    context.fill()
                    context.restore()
                }
                element.y -= ANIMATION_SPEED * scale
                element.rotation += drift * scale
                if (element.y < -300) {
                    element.x = width * Math.random()
                    element.y = height + 300
                    element.rotation = Math.random() * 360
                    element.drift = (Math.random() - Math.random()) * ANIMATION_DRIFT_SPEED
                    element.scale = ANIMATION_MIN_SCALE + ANIMATION_SCALE_DIFF * Math.random()
                }
            })

            if (context) {
                context.fillStyle = gradient || giphyDarkestGrey
                context.globalCompositeOperation = 'source-in'
                context.fillRect(0, 0, width, height)
            }

            animationId = requestAnimationFrame(draw)
        }

        for (let i = 0; i < elementCount; i++) {
            elements.push({
                shape: getAnimationShape(),
                scale: ANIMATION_MIN_SCALE + ANIMATION_SCALE_DIFF * Math.random(),
                rotation: Math.random() * 360,
                drift: (Math.random() - Math.random()) * ANIMATION_DRIFT_SPEED,
                x: width * Math.random(),
                y: height * Math.random(),
            })
        }

        onHeightUpdate(height)
        draw()

        return () => {
            animationId && cancelAnimationFrame(animationId)
        }
    }, [canvasRef, windowWidth])

    return <Canvas ref={canvasRef} />
}

export default AboutPageHeaderAnimation
